import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import {
  isTokenValid,
  clearAuthStorage,
  updateLastActivity,
  getLastActivityMs,
  isSessionActive,
  shouldRefreshTokenSoon,
  getAllowedBranchIds,
  isBranchAllowed,
  IDLE_TIMEOUT_MS
} from '../utils/tokenUtils';
import axios from '../axios';

const AuthContext = createContext(null);

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'keyup',
  'scroll',
  'touchstart',
  'click',
  'input',
  'change'
];

export const AuthProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [allowedBranchIds, setAllowedBranchIds] = useState([]);
  const idleCheckTimerRef = useRef(null);
  const isLoggingOutRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const lastActivityLocalRef = useRef(0);

  const revokeRefreshTokenOnServer = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await axios.post('/User/logout', { refreshToken }, { _skipAuthCheck: true }).catch(() => {});
      }
    } catch {
    }
  };

  const forceLogout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    if (idleCheckTimerRef.current) {
      clearInterval(idleCheckTimerRef.current);
      idleCheckTimerRef.current = null;
    }

    await revokeRefreshTokenOnServer();

    setPermissions([]);
    clearAuthStorage();
    window.location.href = '/login';
  }, []);

  const refreshTokenSilently = useCallback(async () => {
    if (isRefreshingRef.current) return;
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return;

    isRefreshingRef.current = true;
    try {
      const res = await axios.post('/User/refresh-token', { refreshToken }, { _skipAuthCheck: true });
      if (res.data && res.data.success && res.data.data) {
        localStorage.setItem('token', res.data.data.token);
        localStorage.setItem('refreshToken', res.data.data.refreshToken);
      }
    } catch {
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  const handleUserActivity = useCallback(() => {
    if (isLoggingOutRef.current) return;
    const now = Date.now();
    if (now - lastActivityLocalRef.current < 2000) return;
    lastActivityLocalRef.current = now;
    updateLastActivity();

    const token = localStorage.getItem('token');
    if (token && shouldRefreshTokenSoon(token) && !isRefreshingRef.current) {
      refreshTokenSilently();
    }
  }, [refreshTokenSilently]);

  useEffect(() => {
    if (isLoggingOutRef.current) return;

    const storedPerms = localStorage.getItem('permissions');
    if (storedPerms) {
      try {
        setPermissions(JSON.parse(storedPerms));
      } catch (e) {
        console.error("Failed to parse permissions", e);
      }
    }

    setAllowedBranchIds(getAllowedBranchIds());

    const handleBranchesStorage = () => {
      setAllowedBranchIds(getAllowedBranchIds());
    };
    window.addEventListener('storage', handleBranchesStorage);

    const token = localStorage.getItem('token');
    if (token) {
      const lastActivity = getLastActivityMs();
      if (!lastActivity) {
        updateLastActivity();
        lastActivityLocalRef.current = Date.now();
      } else {
        lastActivityLocalRef.current = lastActivity;
      }

      if (!isTokenValid(token)) {
        if (isSessionActive()) {
          refreshTokenSilently();
        } else {
          forceLogout();
          return;
        }
      } else if (!isSessionActive()) {
        forceLogout();
        return;
      }
    }

    const debouncedActivity = () => handleUserActivity();
    ACTIVITY_EVENTS.forEach(evt => {
      document.addEventListener(evt, debouncedActivity, { passive: true });
    });

    idleCheckTimerRef.current = setInterval(() => {
      if (isLoggingOutRef.current) return;
      const token = localStorage.getItem('token');
      if (!token) {
        forceLogout();
        return;
      }

      if (!isSessionActive()) {
        forceLogout();
        return;
      }

      if (!isTokenValid(token)) {
        if (isRefreshingRef.current) return;
        refreshTokenSilently().then(() => {
          const t = localStorage.getItem('token');
          if (!t || !isTokenValid(t)) {
            forceLogout();
          }
        });
        return;
      }

      if (shouldRefreshTokenSoon(token) && !isRefreshingRef.current) {
        refreshTokenSilently();
      }
    }, 15000);

    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue) {
        forceLogout();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isLoggingOutRef.current) {
        handleUserActivity();
        const token = localStorage.getItem('token');
        if (!token || !isTokenValid(token) || !isSessionActive()) {
          forceLogout();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      ACTIVITY_EVENTS.forEach(evt => {
        document.removeEventListener(evt, debouncedActivity);
      });
      if (idleCheckTimerRef.current) {
        clearInterval(idleCheckTimerRef.current);
      }
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('storage', handleBranchesStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleUserActivity, forceLogout, refreshTokenSilently]);

  const hasPermission = (requiredPermission) => {
    if (!requiredPermission) return true;
    return permissions.includes(requiredPermission);
  };

  const hasAnyPermission = (requiredPermissions) => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    return requiredPermissions.some(perm => permissions.includes(perm));
  };

  const hasBranchAccess = (branchId) => {
    if (branchId == null) return false;
    return isBranchAllowed(branchId);
  };

  const setAuthData = (data) => {
    if (data && data.permissions) {
      setPermissions(data.permissions);
      localStorage.setItem('permissions', JSON.stringify(data.permissions));
    }
    const branches = getAllowedBranchIds();
    setAllowedBranchIds(branches);
    isLoggingOutRef.current = false;
    updateLastActivity();
    lastActivityLocalRef.current = Date.now();
  };

  const logout = useCallback(async () => {
    await forceLogout();
  }, [forceLogout]);

  return (
    <AuthContext.Provider value={{
      permissions,
      allowedBranchIds,
      hasPermission,
      hasAnyPermission,
      hasBranchAccess,
      setAuthData,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const PermissionGuard = ({ permission, children, fallback = null }) => {
  const { hasPermission } = useAuth();
  if (hasPermission(permission)) {
    return children;
  }
  return fallback;
};
